import type { ReactNode } from 'react';
import styles from './AppShell.module.css';

type AppShellProps = {
  nav: ReactNode;
  main: ReactNode;
  rail: ReactNode;
};

/**
 * Responsive app shell. Adapts across the 6c tier matrix:
 *
 *   Comfortable (601 – 1023 px)  → single 720-px column, nav + rail hidden
 *   Default     (1024 – 1279 px) → 240 / 1fr / 300, capped at 1280
 *   Wide        (1280 – 1919 px) → 240 / 1fr / 320, capped at 1600
 *   Ultra       (≥ 1920 px)      → 240 / 1fr / 320, capped at 1920
 *
 * The nav column IS `--nav-width` (styles/tokens.css). The numbers above
 * mirror the tokens and drifted from them once already, so they move together
 * or not at all; `AppShell.test.tsx` pins the pair.
 *
 * R-Ultra-4 (CD 2026-09-09) — the Ultra four-pane retires. The optional
 * `seasonRail` slot and its `.shellWithSeasonRail` grid are gone; its three
 * consumers (Today, Team, Races) carry their content in the RIGHT rail,
 * Ultra-gated by their own route CSS. Three panes at every desktop tier.
 *
 * `data-shell-variant="3-pane"` stays as a CONSTANT attribute: it is the
 * selector the desktop-rail route tests hang on, and a constant keeps that
 * contract honest now that no variant switch exists.
 *
 * ## Skip to content (UX review 2026-09-08)
 *
 * The SideNav puts about eight tab stops between the top of the page and the
 * content, on every shell route, on every navigation. The first focusable
 * element in the shell is now a visually hidden "Skip to content" link that
 * shows itself on focus and moves focus to the `<main>` (`id="main"`,
 * `tabIndex={-1}` so a fragment jump can land on it).
 *
 * It rides INSIDE the nav `<aside>` rather than as a fourth child of the
 * grid, and that placement is load-bearing: six route test files pin the
 * shell's children to exactly `[ASIDE, MAIN, ASIDE]`, and four of them also
 * pin the HELD nav slot to zero children (the B87-fix contract: a wedged
 * `/api/relationships` costs the rail, never the page). So the link renders
 * only once the nav slot has content — which is also when there is anything
 * to skip.
 *
 * Mobile (≤ 600 px) bypasses AppShell entirely — pages render their own
 * stacked column. AppShell at < 601 px falls through to a 100%-width
 * single column, but in practice no caller renders it there.
 */
export function AppShell({ nav, main, rail }: AppShellProps) {
  return (
    <div className={styles.shell} data-shell-variant="3-pane">
      <aside className={styles.nav}>
        {nav != null && (
          <a className={styles.skipLink} href="#main">
            Skip to content
          </a>
        )}
        {nav}
      </aside>
      <main className={styles.main} id="main" tabIndex={-1}>
        {main}
      </main>
      <aside className={styles.rail}>{rail}</aside>
    </div>
  );
}
